import type { ExpectationAutomation, ExpectationAutomationPayload } from '@app/src/types/expectations'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { RiskStatus } from '@app/src/types/resourceExplorer'
import { isDateInFuture } from '@app/src/utils/helpersTs'
import type { DeadlineType, ExpectationAutomationFormData } from './types'
import { ExpectationAutomationFormField, SupplierRiskOption } from './types'

export const getLevelsBelowTarget = (target: number): number[] =>
  Array.from({ length: target - 1 }, (_, index) => index + 1)

const RISK_STATUS_BY_SUPPLIER_RISK_OPTION: Record<SupplierRiskOption, RiskStatus | null> = {
  [SupplierRiskOption.Any]: null,
  [SupplierRiskOption.Extreme]: RiskStatus.High,
  [SupplierRiskOption.HighOrExtreme]: RiskStatus.Medium,
  [SupplierRiskOption.MediumOrHigher]: RiskStatus.Low,
}

const SUPPLIER_RISK_OPTION_BY_RISK_STATUS: Partial<Record<RiskStatus, SupplierRiskOption>> = {
  [RiskStatus.High]: SupplierRiskOption.Extreme,
  [RiskStatus.Medium]: SupplierRiskOption.HighOrExtreme,
  [RiskStatus.Low]: SupplierRiskOption.MediumOrHigher,
}

const toSupplierRiskOption = (value: RiskStatus | null | undefined): SupplierRiskOption =>
  (value && SUPPLIER_RISK_OPTION_BY_RISK_STATUS[value]) || SupplierRiskOption.Any

const toBackendRiskStatus = (value: SupplierRiskOption): RiskStatus | null => RISK_STATUS_BY_SUPPLIER_RISK_OPTION[value]

const SUPPLIER_RISK_LABEL_ID_BY_VALUE: Record<SupplierRiskOption, string> = {
  [SupplierRiskOption.Any]: 'form.createExpectationAutomation.riskStatus.anyOption',
  [SupplierRiskOption.Extreme]: 'schemas.risk.status.FExtreme',
  [SupplierRiskOption.HighOrExtreme]: 'form.createExpectationAutomation.riskStatus.highOption',
  [SupplierRiskOption.MediumOrHigher]: 'form.createExpectationAutomation.riskStatus.mediumOption',
}

export const getSupplierRiskSummaryLabelId = (value: SupplierRiskOption): string | undefined =>
  SUPPLIER_RISK_LABEL_ID_BY_VALUE[value]

const CARD_SUMMARY_LABEL_ID_BY_RISK_STATUS: Partial<Record<RiskStatus, string>> = {
  [RiskStatus.High]: 'automatedSendout.expectation.summaryExtreme',
  [RiskStatus.Medium]: 'automatedSendout.expectation.summaryHighOrExtreme',
  [RiskStatus.Low]: 'automatedSendout.expectation.summaryMediumOrHigher',
}

export const getExpectationCardSummaryLabelId = (value: RiskStatus | null | undefined): string =>
  (value && CARD_SUMMARY_LABEL_ID_BY_RISK_STATUS[value]) || 'automatedSendout.expectation.summaryAnyRisk'

const DEADLINE_YEAR_LABEL_ID_BY_OFFSET: Record<1 | 2 | 3, string> = {
  1: 'form.createExpectationAutomation.deadline.year1Option',
  2: 'form.createExpectationAutomation.deadline.year2Option',
  3: 'form.createExpectationAutomation.deadline.year3Option',
}

export const getDeadlineYearLabelId = (deadlineType: DeadlineType): string | undefined =>
  deadlineType === 'custom' ? undefined : DEADLINE_YEAR_LABEL_ID_BY_OFFSET[deadlineType]

export const buildDefaultFormValues = (): ExpectationAutomationFormData => ({
  [ExpectationAutomationFormField.AssessmentTemplateId]: null,
  [ExpectationAutomationFormField.TargetAssessmentLevel]: 4,
  [ExpectationAutomationFormField.HighestAllowedProviderRiskStatus]: SupplierRiskOption.HighOrExtreme,
  [ExpectationAutomationFormField.DeadlineType]: 1,
  [ExpectationAutomationFormField.CustomDueDate]: null,
  [ExpectationAutomationFormField.ImportantKpis]: [],
})

export const isFutureDate = (value: string | null | undefined): boolean => (value ? isDateInFuture(value) : false)

export const isExpectationAutomationActive = (automation: ExpectationAutomation): boolean =>
  !automation.customDueDate || isFutureDate(automation.customDueDate)

const normaliseDeadlineType = (offset: number | undefined): DeadlineType =>
  offset === 1 || offset === 2 || offset === 3 ? offset : 1

export const mapAutomationToFormData = (automation: ExpectationAutomation): ExpectationAutomationFormData => {
  const hasCustomDueDate = Boolean(automation.customDueDate)
  return {
    [ExpectationAutomationFormField.Id]: automation.id,
    [ExpectationAutomationFormField.AssessmentTemplateId]: automation.assessmentTemplateId,
    [ExpectationAutomationFormField.TargetAssessmentLevel]: automation.targetAssessmentLevel,
    [ExpectationAutomationFormField.HighestAllowedProviderRiskStatus]: toSupplierRiskOption(
      automation.highestAllowedProviderRiskStatus,
    ),
    [ExpectationAutomationFormField.DeadlineType]: hasCustomDueDate
      ? 'custom'
      : normaliseDeadlineType(automation.deadlineYearsOffset),
    [ExpectationAutomationFormField.CustomDueDate]: automation.customDueDate ?? null,
    [ExpectationAutomationFormField.ImportantKpis]: automation.importantQuestions ?? [],
    [ExpectationAutomationFormField.ApplyToExisting]: false,
  }
}

/** Whether the target is the template's top level, where "or higher" would not apply. */
export const isTopTargetLevel = (target: number, templateLevels: number | undefined): boolean =>
  target >= (templateLevels ?? 5)

/** An edited automation keeps its own assessment, even one deactivated since it was created. */
export const withEditedAutomationTemplate = (
  offeredTemplates: AssessmentTemplate[],
  editedTemplate: AssessmentTemplate | undefined,
): AssessmentTemplate[] =>
  !editedTemplate || offeredTemplates.some(template => template.id === editedTemplate.id)
    ? offeredTemplates
    : [...offeredTemplates, editedTemplate]

export const canSubmit = (values: ExpectationAutomationFormData): boolean => {
  const deadlineType = values[ExpectationAutomationFormField.DeadlineType]
  if (deadlineType === undefined) return false
  if (deadlineType === 'custom') {
    return isFutureDate(values[ExpectationAutomationFormField.CustomDueDate])
  }
  return true
}

export const buildSubmissionPayload = (
  values: ExpectationAutomationFormData,
  assessmentTemplateId: number,
): ExpectationAutomationPayload & { id?: number } => {
  const deadlineType = values[ExpectationAutomationFormField.DeadlineType]
  const customDate = values[ExpectationAutomationFormField.CustomDueDate]
  const isCustom = deadlineType === 'custom'
  const riskStatus = values[ExpectationAutomationFormField.HighestAllowedProviderRiskStatus]
  const importantKpis = values[ExpectationAutomationFormField.ImportantKpis]
  const id = values[ExpectationAutomationFormField.Id]
  return {
    ...(id ? { id } : {}),
    assessmentTemplateId,
    targetAssessmentLevel: values[ExpectationAutomationFormField.TargetAssessmentLevel],
    highestAllowedProviderRiskStatus: toBackendRiskStatus(riskStatus),
    deadlineYearsOffset: isCustom ? null : (deadlineType as number),
    customDueDate: isCustom && customDate ? customDate : null,
    applyToExisting: values[ExpectationAutomationFormField.ApplyToExisting] ?? false,
    ...(id || importantKpis.length > 0 ? { importantQuestions: importantKpis } : {}),
  }
}
